import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Users, Calendar, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cities = [
  'Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Kolkata', 'Hyderabad',
  'Pune', 'Ahmedabad', 'Jaipur', 'Goa', 'Kochi', 'Chandigarh',
  'Lucknow', 'Indore', 'Bhopal', 'Nagpur', 'Visakhapatnam', 'Surat'
];

const travelFrequencies = [
  'One-time',
  'Once a month',
  'Twice a month',
  'Once a quarter',
  'Multiple times a year'
];

export default function SubscriptionPlans() {
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    user_phone: '',
    from_city: '',
    to_city: '',
    number_of_tickets: '',
    travel_frequency: '',
    additional_notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/travel-interest`, {
        ...formData,
        number_of_tickets: parseInt(formData.number_of_tickets)
      });
      toast.success('Thank you! We\'ll contact you soon with a customized plan.');
      setSubmitted(true);
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 pb-16 flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl w-full"
          >
            <Card className="p-12 border-slate-200 text-center" data-testid="success-message">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-4">Interest Submitted Successfully!</h1>
              <p className="text-lg text-slate-600 mb-8">
                Thank you for your interest in AirVia. Our team will review your requirements and contact you within 24 hours with a customized subscription plan tailored to your travel needs.
              </p>
              <div className="bg-slate-50 rounded-xl p-6 mb-6">
                <p className="text-sm text-slate-600 mb-2">We'll create a personalized plan based on:</p>
                <div className="space-y-2 text-left">
                  <p className="text-slate-700">• Route: <span className="font-semibold">{formData.from_city} → {formData.to_city}</span></p>
                  <p className="text-slate-700">• Tickets needed: <span className="font-semibold">{formData.number_of_tickets}</span></p>
                  <p className="text-slate-700">• Travel frequency: <span className="font-semibold">{formData.travel_frequency}</span></p>
                </div>
              </div>
              <Button
                onClick={() => window.location.href = '/'}
                className="rounded-full bg-amber-600 hover:bg-amber-700 text-white px-8 py-6"
              >
                Back to Home
              </Button>
            </Card>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-24 pb-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" data-testid="interest-form-title">
              Tell Us Your Travel Needs
            </h1>
            <p className="text-lg text-slate-600">
              We'll create a customized subscription plan based on your sector and ticket requirements
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="p-8 border-slate-200">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h2 className="text-xl font-bold text-slate-900 mb-4">Personal Information</h2>
                  
                  <div>
                    <Label htmlFor="name" className="text-slate-900 mb-2 block">Full Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Enter your full name"
                      value={formData.user_name}
                      onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                      className="py-6 rounded-lg border-slate-300"
                      required
                      data-testid="interest-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email" className="text-slate-900 mb-2 block">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.user_email}
                        onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                        className="py-6 rounded-lg border-slate-300"
                        required
                        data-testid="interest-email-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-slate-900 mb-2 block">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+91XXXXXXXXXX"
                        value={formData.user_phone}
                        onChange={(e) => setFormData({ ...formData, user_phone: e.target.value })}
                        className="py-6 rounded-lg border-slate-300"
                        required
                        data-testid="interest-phone-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Travel Details */}
                <div className="space-y-4 pt-6 border-t border-slate-200">
                  <h2 className="text-xl font-bold text-slate-900 mb-4">Travel Requirements</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="from" className="text-slate-900 mb-2 block">From (Departure City) *</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                        <select
                          id="from"
                          value={formData.from_city}
                          onChange={(e) => setFormData({ ...formData, from_city: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-amber-600"
                          required
                          data-testid="interest-from-select"
                        >
                          <option value="">Select departure city</option>
                          {cities.map(city => (
                            <option key={city} value={city}>{city}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="to" className="text-slate-900 mb-2 block">To (Arrival City) *</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                        <select
                          id="to"
                          value={formData.to_city}
                          onChange={(e) => setFormData({ ...formData, to_city: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-amber-600"
                          required
                          data-testid="interest-to-select"
                        >
                          <option value="">Select arrival city</option>
                          {cities.map(city => (
                            <option key={city} value={city}>{city}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="tickets" className="text-slate-900 mb-2 block">Number of Tickets *</Label>
                      <div className="relative">
                        <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="tickets"
                          type="number"
                          min="1"
                          placeholder="e.g., 4"
                          value={formData.number_of_tickets}
                          onChange={(e) => setFormData({ ...formData, number_of_tickets: e.target.value })}
                          className="pl-10 py-6 rounded-lg border-slate-300"
                          required
                          data-testid="interest-tickets-input"
                        />
                      </div>
                      <p className="text-sm text-slate-500 mt-1">How many tickets do you need?</p>
                    </div>

                    <div>
                      <Label htmlFor="frequency" className="text-slate-900 mb-2 block">Travel Frequency *</Label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                        <select
                          id="frequency"
                          value={formData.travel_frequency}
                          onChange={(e) => setFormData({ ...formData, travel_frequency: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-amber-600"
                          required
                          data-testid="interest-frequency-select"
                        >
                          <option value="">Select frequency</option>
                          {travelFrequencies.map(freq => (
                            <option key={freq} value={freq}>{freq}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="notes" className="text-slate-900 mb-2 block">Additional Notes (Optional)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Any specific requirements or preferences..."
                      value={formData.additional_notes}
                      onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                      className="min-h-[100px] rounded-lg border-slate-300"
                      data-testid="interest-notes-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg font-semibold"
                  data-testid="interest-submit-btn"
                >
                  {loading ? 'Submitting...' : 'Submit Interest'}
                  <Send className="ml-2 w-5 h-5" />
                </Button>
              </form>

              <div className="mt-8 p-6 bg-sky-50 rounded-xl border border-sky-200">
                <h3 className="font-semibold text-slate-900 mb-3">What happens next?</h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
                    <span>Our team will review your travel requirements</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
                    <span>We'll create a customized subscription plan with pricing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
                    <span>You'll receive a personalized quote within 24 hours</span>
                  </li>
                </ul>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
